import React from 'react';

import classNames from 'classnames';

import DynamicColumnsTableColumnHeader from 'components/DynamicColumnsTable/DynamicColumnsTableColumnHeader';

import CMASummaryTableDataColumn from './CMASummaryTableDataColumn';
import CMASummaryTableTitleColumn from './CMASummaryTableTitleColumn';
import {
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_CARE_FEE_DATA_ROW,
  CMA_SUMMARY_COMPETITOR_AVERAGE_LABEL,
  CMA_SUMMARY_DATA_ROW_TYPE,
  CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
  CMA_SUMMARY_TABLE_COLUMNS_OFFSET,
} from './constants';
import { getCommunityNameTestId } from './testIdUtils';

export const generateCMASummaryTableColumns = ({
  community,
  competitors,
  filteredCompetitors,
  competitorsMinIndex,
  competitorsMaxIndex,
  displayedNumberOfCompetitors,
  expandedRowKeys,
  setExpandedRowKeys,
  scrollCompetitorsLeft,
  scrollCompetitorsRight,
  paginate,
  showCompetitorAverage,
}: {
  community: any;
  competitors: any[];
  filteredCompetitors: any[];
  scrollCompetitorsLeft: () => void;
  scrollCompetitorsRight: () => void;
  expandedRowKeys: string[];
  setExpandedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  competitorsMinIndex: number;
  competitorsMaxIndex: number;
  displayedNumberOfCompetitors: number;
  paginate?: boolean;
  showCompetitorAverage?: boolean;
}) => {
  const disableLeftScroll = competitorsMinIndex === 0;
  const disableRightScroll =
    competitorsMaxIndex === (competitors?.length || 0) - 1 || competitors?.length + 1 <= competitorsMaxIndex;

  const competitorsToDisplay = paginate ? filteredCompetitors ?? [] : competitors ?? [];

  const columns: any[] = [
    {
      title: '',
      render: (value: any, cmaSummaryRow: any) => (
        <CMASummaryTableTitleColumn
          cmaSummaryRow={cmaSummaryRow.rowContent}
          expandedRowKeys={expandedRowKeys}
          setExpandedRowKeys={setExpandedRowKeys}
        />
      ),
    },
    {
      title: () => <span data-test-id={getCommunityNameTestId()}>{community?.comunity_name}</span>,
      render: (value: any, cmaSummaryRow: any) => (
        <CMASummaryTableDataColumn
          cmaSummaryRow={cmaSummaryRow.rowContent}
          index={1}
          expandedRowKeys={expandedRowKeys}
        />
      ),
    },
  ];

  if (showCompetitorAverage) {
    columns.push({
      title: CMA_SUMMARY_COMPETITOR_AVERAGE_LABEL,
      onHeaderCell: () => ({
        className: paginate ? 'cma-summary-average-before-paginated-competitors' : '',
      }),
      render: (value: any, cmaSummaryRow: any) => (
        <CMASummaryTableDataColumn
          cmaSummaryRow={cmaSummaryRow.rowContent}
          index={CMA_SUMMARY_TABLE_COLUMNS_OFFSET}
          expandedRowKeys={expandedRowKeys}
        />
      ),
    });
  }

  competitorsToDisplay.forEach((competitor: any, index: number) => {
    const indexWithOffset =
      CMA_SUMMARY_TABLE_COLUMNS_OFFSET + (showCompetitorAverage ? 1 : 0) + competitorsMinIndex + index;
    const competitorIndex = competitorsMinIndex + index;
    columns.push({
      title: () => (
        <DynamicColumnsTableColumnHeader
          index={index}
          community={competitor}
          filteredCompetitorsCount={filteredCompetitors?.length ?? 0}
          totalCompetitorsCount={competitors?.length || 0}
          scrollLeft={scrollCompetitorsLeft}
          scrollRight={scrollCompetitorsRight}
          disableLeft={disableLeftScroll}
          disableRight={disableRightScroll}
          showScrolls={paginate}
          displayedNumberOfCompetitors={displayedNumberOfCompetitors}
          dataTestId={getCommunityNameTestId(competitorIndex)}
        />
      ),
      render: (value: any, cmaSummaryRow: any) => (
        <CMASummaryTableDataColumn
          cmaSummaryRow={cmaSummaryRow.rowContent}
          index={indexWithOffset}
          expandedRowKeys={expandedRowKeys}
        />
      ),
    });
  });

  return columns;
};

export const getAntdRowClassName = (record: any, index: number) => {
  if (record?.rowContent?.length) {
    const rowMetadata = record?.rowContent[0];
    return classNames({
      'cma-summary-apartment-type-data-row': [
        CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
        CMA_SUMMARY_CARE_FEE_DATA_ROW,
        CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
        CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
      ].includes(rowMetadata.type),
      'room-comparison-row': [
        CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
        CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
      ].includes(rowMetadata.type),
      'review-row': rowMetadata.type === CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS,
    });
  }
  return '';
};
